import { Box, Chip, Theme, Tooltip, Typography, useTheme } from "@mui/material";
import { ReactNode } from "react";
import { TFunction } from "i18next";
import { useTranslation } from "react-i18next";
import { ScoreSourceMeta, ScoreSourceKind } from "src/api/client-api/olapScores/olapScoresSchema";

/**
 * Compact badge that surfaces *where* a score came from in the cascade
 * (cf. backend `IngredientScoreSourceMetaResolver`). Designed to live
 * top-right of a score cell as an overlay (cf. plan §FE-21) or alongside
 * the aggregate-row "En moyenne" pill.
 *
 * Two consumption modes:
 *   - `meta`         → single descriptor (per-cell badge, legacy behaviour).
 *   - `contributors` → list of distinct contributors (aggregate-row badge,
 *     introduced for the Apr-25 product feedback on mixed sources). When set,
 *     the chip renders one visible label/initial reflecting the dominant kind
 *     and composes a tooltip enumerating every contributor (e.g. "Score basé
 *     sur la référence fournisseur Valrhona et la référence plateforme.").
 *
 * Visual contract (initial glyphs):
 *   - any `supplier_reference` contributor → "F" (Fournisseur)
 *   - else any `ingredient_reference`      → "P" (Plateforme)
 *   - else `sure`                          → "S" (Sure brand)
 *   - pure `ssq`/`none` rows               → no chip (precise data path)
 *
 * The badge initials are mnemonic *brand-style* glyphs — they're paired with
 * the localized tooltip/label so screen-reader users still get the full copy.
 * "Sure" stays "S" across locales because it is a product brand name.
 *
 * Why a single shared component:
 *   The same source metadata is consumed in at least four places (grid cells,
 *   ScatterScoreBox, ScoreCards, MultipleSupplierScores toggle row) and the
 *   source-kind → label/colour mapping must stay strictly identical.
 *   Centralizing it here is the only way to keep the UX coherent when we
 *   later iterate on copy or colours.
 */
type Props = {
    meta?: ScoreSourceMeta | null;
    /**
     * Distinct contributors to the aggregate row, deduped by `(source, owner)`.
     * Takes precedence over `meta` when set. Pass `[]` to explicitly render
     * nothing (e.g. when the OLAP payload is missing).
     */
    contributors?: ScoreSourceMeta[];
    /**
     * When true, render an icon-sized circular badge (used for the cell-overlay
     * variant). When false (default), render a labelled chip suitable for
     * tooltips or list rows.
     */
    compact?: boolean;
};

export const ScoreSourceChip = ({ meta, contributors, compact = false }: Props) => {
    const { t } = useTranslation();
    const theme = useTheme();

    // Normalize the inputs — `contributors` always wins when provided. The
    // legacy `meta` API is kept for the per-cell badge call sites that don't
    // enumerate contributors.
    const list: ScoreSourceMeta[] = contributors ?? (meta ? [meta] : []);

    // Keep provenance badges hidden until the product decision to restore them.
    // eslint-disable-next-line no-constant-condition
    if (true) return null;

    const renderable = list.filter((c) => c.source !== "ssq" && c.source !== "none");

    if (renderable.length === 0) return null;

    const hasSsq = list.some((c) => c.source === "ssq");
    const dominantSource = pickDominantSource(renderable);
    const palette = getPaletteForSource(theme, dominantSource);
    const label = composeLabel(t, renderable);
    const tooltip = composeTooltip(t, renderable, hasSsq);

    if (compact) {
        return (
            <Tooltip title={tooltip} arrow>
                <Box
                    component="span"
                    aria-label={tooltip}
                    sx={{
                        display: "inline-flex",
                        alignItems: "center",
                        justifyContent: "center",
                        width: 20,
                        height: 20,
                        borderRadius: "50%",
                        backgroundColor: palette.background,
                        color: palette.color,
                        border: `1px solid ${palette.border}`,
                        fontSize: 11,
                        fontWeight: 600,
                        lineHeight: 1,
                    }}
                >
                    {getInitialForSource(dominantSource)}
                </Box>
            </Tooltip>
        );
    }

    return (
        <Tooltip title={tooltip} arrow>
            <Chip
                size="small"
                label={
                    <Typography variant="caption" component="span">
                        {label}
                    </Typography>
                }
                aria-label={tooltip}
                sx={{
                    height: 22,
                    backgroundColor: palette.background,
                    color: palette.color,
                    border: `1px solid ${palette.border}`,
                    "& .MuiChip-label": { px: 1 },
                }}
            />
        </Tooltip>
    );
};

const getInitialForSource = (source: ScoreSourceKind): ReactNode => {
    switch (source) {
        case "supplier_reference":
            return "F";
        case "ingredient_reference":
            return "P";
        case "sure":
            return "S";
        default:
            return null;
    }
};

/**
 * Pick the dominant source kind for the badge glyph and palette. Priority is
 * stable so the same payload always yields the same chip:
 *   supplier_reference > ingredient_reference > sure
 *
 * The aggregate row may have several kinds at once (Step 3 cascade — see
 * `WeightedAverageHelper`). The chip can only display one initial; the full
 * detail lives in the tooltip.
 */
const pickDominantSource = (contributors: ScoreSourceMeta[]): ScoreSourceKind => {
    if (contributors.some((c) => c.source === "supplier_reference")) return "supplier_reference";
    if (contributors.some((c) => c.source === "ingredient_reference")) return "ingredient_reference";
    if (contributors.some((c) => c.source === "sure")) return "sure";
    return "none";
};

/**
 * Shorthand label rendered inside the chip body (non-compact mode).
 * Single contributor → keep the existing per-source wording so unrelated UIs
 * (grid cells, etc.) stay visually unchanged. Multiple contributors → use a
 * generic "Sources multiples" pill — the detail lives in the tooltip.
 */
const composeLabel = (t: TFunction, contributors: ScoreSourceMeta[]): string => {
    if (contributors.length === 1) {
        return getLabelForSource(t, contributors[0]);
    }

    const allSupplierRefSameOwner =
        contributors.every((c) => c.source === "supplier_reference") &&
        contributors.every((c) => c.owner?.id === contributors[0].owner?.id);
    if (allSupplierRefSameOwner) {
        return getLabelForSource(t, contributors[0]);
    }

    return t("score_source.multiple.label", { defaultValue: "Multiple sources" });
};

const getLabelForSource = (t: TFunction, meta: ScoreSourceMeta): string => {
    switch (meta.source) {
        case "supplier_reference":
            return meta.owner?.name ?
                    t("score_source.supplier_reference.label_with_owner", {
                        defaultValue: "Supplier: {{name}}",
                        name: meta.owner.name,
                    })
                :   t("score_source.supplier_reference.label", { defaultValue: "Supplier reference" });
        case "ingredient_reference":
            return t("score_source.ingredient_reference.label", { defaultValue: "Platform reference" });
        case "sure":
            return t("score_source.sure.label", { defaultValue: "Sure reference" });
        default:
            return "";
    }
};

/**
 * Compose the chip tooltip describing every distinct contributor that fed the
 * aggregate row score. Three flavours:
 *
 *   - 1 contributor, no SSQ → reuse the per-source single-line wording
 *     ("Score provenant de la référence fournisseur Valrhona.").
 *   - N contributors, no SSQ → "Score basé sur <list>." enumerating each
 *     contributor inline (e.g. "la référence fournisseur Valrhona et la
 *     référence plateforme").
 *   - SSQ + at least one reference → "Certains scores proviennent d'un
 *     questionnaire, d'autres de <list>." matching the user's wording in the
 *     Apr-25 feedback.
 *
 * Pure SSQ rows render no chip (filtered upstream); they never reach this fn.
 */
const composeTooltip = (t: TFunction, contributors: ScoreSourceMeta[], hasSsq: boolean): string => {
    const sourcesList = humanJoin(
        t,
        contributors.map((c) => describeContributorInline(t, c)),
    );

    if (hasSsq) {
        return t("score_source.composite.with_ssq.tooltip", {
            defaultValue: "Some scores come from a questionnaire, others from {{sources}}.",
            sources: sourcesList,
        });
    }

    if (contributors.length === 1) {
        return getTooltipForSource(t, contributors[0]);
    }

    return t("score_source.composite.tooltip", {
        defaultValue: "Score based on {{sources}}.",
        sources: sourcesList,
    });
};

const getTooltipForSource = (t: TFunction, meta: ScoreSourceMeta): string => {
    switch (meta.source) {
        case "supplier_reference":
            return meta.owner?.name ?
                    t("score_source.supplier_reference.tooltip_with_owner", {
                        defaultValue: "Score from {{name}}'s supplier reference.",
                        name: meta.owner.name,
                    })
                :   t("score_source.supplier_reference.tooltip", {
                        defaultValue: "Score from a supplier reference.",
                    });
        case "ingredient_reference":
            return t("score_source.ingredient_reference.tooltip", {
                defaultValue: "Score from a default ingredient (platform reference).",
            });
        case "sure":
            return t("score_source.sure.tooltip", {
                defaultValue: "Score from the Sure reference.",
            });
        default:
            return "";
    }
};

/**
 * Inline description used INSIDE composite tooltips ("la référence ..."). Differs
 * from `getTooltipForSource` which produces stand-alone sentences. Keeping the
 * two split lets the composite sentence read fluently in French and English
 * without awkward sub-sentence punctuation.
 */
const describeContributorInline = (t: TFunction, meta: ScoreSourceMeta): string => {
    switch (meta.source) {
        case "supplier_reference":
            return meta.owner?.name ?
                    t("score_source.composite.supplier_reference.with_owner", {
                        defaultValue: "the {{name}} supplier reference",
                        name: meta.owner.name,
                    })
                :   t("score_source.composite.supplier_reference", {
                        defaultValue: "a supplier reference",
                    });
        case "ingredient_reference":
            return t("score_source.composite.ingredient_reference", {
                defaultValue: "the platform reference",
            });
        case "sure":
            return t("score_source.composite.sure", { defaultValue: "the Sure reference" });
        default:
            return "";
    }
};

/**
 * Locale-aware list join ("a, b et c"). Uses `Intl.ListFormat` when available
 * (modern browsers + Node ≥ 12) and falls back to a hardcoded "X, Y and Z" /
 * "X, Y et Z" otherwise. The conjunction is sourced from i18n so right-to-left
 * locales can override.
 */
const humanJoin = (t: TFunction, items: string[]): string => {
    if (items.length === 0) return "";
    if (items.length === 1) return items[0];

    if (typeof Intl !== "undefined" && typeof Intl.ListFormat === "function") {
        try {
            // `locale.code` is a per-locale key that holds the BCP-47 short tag
            // ("fr", "en", "es", ...). We coerce to string + fall back to "fr"
            // because i18next's strict resource types narrow the return type to a
            // literal, but `Intl.ListFormat` only accepts a generic `string`.
            const localeCode = String(t("locale.code", { defaultValue: "fr" }) || "fr");
            return new Intl.ListFormat(localeCode.split("-")[0], { type: "conjunction" }).format(items);
        } catch {
            // Fall through to manual join below.
        }
    }

    const conjunction = t("score_source.composite.conjunction", { defaultValue: "and" });
    if (items.length === 2) return `${items[0]} ${conjunction} ${items[1]}`;
    return `${items.slice(0, -1).join(", ")} ${conjunction} ${items[items.length - 1]}`;
};

type ChipPalette = { background: string; color: string; border: string };

const getPaletteForSource = (theme: Theme, source: ScoreSourceKind): ChipPalette => {
    switch (source) {
        case "supplier_reference":
            return {
                background: theme.palette.primary.light ?? "#E3F2FD",
                color: theme.palette.primary.dark ?? "#0D47A1",
                border: theme.palette.primary.main ?? "#1976D2",
            };
        case "ingredient_reference":
            return {
                background: theme.palette.grey[100],
                color: theme.palette.grey[800],
                border: theme.palette.grey[400],
            };
        case "sure":
            return {
                background: theme.palette.grey[50],
                color: theme.palette.grey[700],
                border: theme.palette.grey[300],
            };
        default:
            return {
                background: "transparent",
                color: theme.palette.text.primary,
                border: "transparent",
            };
    }
};
